/**
 * recurringTaxCoupling — reine Satz↔Steuerkategorie-Kopplung für Recurring-Vorlagen.
 *
 * Der Recurring-Vorlagen-Editor arbeitet direkt mit dem EN-16931 BT-151-Code
 * (`taxCategory`) statt mit dem feingranularen `InvoiceTaxCategoryKey` des
 * Hauptformulars. Diese Helfer spiegeln denselben Kopplungs-Contract wie
 * `InvoiceItemsForm` (`getRateConstraintForCategoryCode` / `ratesLocked`), damit
 * FE-Auswahl und der server-seitige `ApiInvoiceValidator` (S ⇒ 19/7,
 * Nullsatz/Befreiung ⇒ 0, E ⇒ Befreiungsgrund mit Norm-Nr.) nicht auseinanderlaufen.
 *
 * Bewusst pure & seiteneffektfrei (Resolver-Pattern) — trivial testbar ohne React.
 *
 * @module recurringTaxCoupling
 */

import {
  getRateConstraintForCategoryCode,
  type InvoiceTaxCategoryCode,
} from './taxCategoryCatalog';

/** Minimaler Positions-Shape für die Satz-Plausibilisierung. */
export interface RateCoerceableItem {
  taxCategory?: InvoiceTaxCategoryCode | null;
  taxRate: number;
}

/**
 * Zwingt den USt-Satz einer Position in den für ihre Steuerkategorie zulässigen
 * Bereich (Validator-Contract, `ApiInvoiceValidator`):
 *   - Nullsatz-/Befreiungs-Kategorien (Z/E/AE/K/G) ⇒ Satz auf 0 gesperrt.
 *   - `S` (Standard) mit unzulässigem Satz (z. B. 0 aus einer Alt-Vorlage) ⇒ 19.
 * Ein bereits gültiger Satz (19/7 unter `S`, 0 unter Nullsatz) bleibt unangetastet.
 *
 * Gibt bei Bedarf ein NEUES Objekt zurück (immutabel), sonst die Eingabe unverändert.
 *
 * @param item Position mit (ggf. entkoppeltem) Satz.
 * @returns Position mit plausibilisiertem `taxRate`.
 */
export function coerceItemRateToCategory<T extends RateCoerceableItem>(item: T): T {
  const constraint = getRateConstraintForCategoryCode(item.taxCategory);
  if (constraint.ratesLocked) {
    return item.taxRate === 0 ? item : { ...item, taxRate: 0 };
  }
  if (!constraint.allowedRates.includes(Number(item.taxRate))) {
    return { ...item, taxRate: 19 };
  }
  return item;
}

/**
 * Prüft fail-closed, ob einer §4-steuerfreien Position (`taxCategory === 'E'`) der
 * Pflicht-Befreiungsgrund mit konkreter Norm-Nummer fehlt.
 *
 * Spiegelt exakt die Validator-Regel (`exemption_reason_required`): der Grund muss
 * gesetzt sein UND mindestens eine Ziffer enthalten (z. B. "§ 4 Nr. 21 UStG").
 * Für alle anderen Kategorien immer `false` (kein Grund nötig).
 *
 * @param category BT-151-Kategorie-Code der Position.
 * @param exemptionReason Erfasster Befreiungsgrund (oder leer/null).
 * @returns `true`, wenn `E` gewählt ist und ein gültiger Grund fehlt.
 */
export function recurringExemptionReasonMissing(
  category: InvoiceTaxCategoryCode | null | undefined,
  exemptionReason: string | null | undefined
): boolean {
  if ((category ?? 'S') !== 'E') return false;
  return !/\d/.test((exemptionReason ?? '').trim());
}
